import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import type { FuenteDeLaPropuesta as Fuente } from "@/lib/rellenoConIa";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { FuenteDeLaPropuesta } from "./FuenteDeLaPropuesta";

/**
 * Las filas que la IA encontró, para revisarlas antes de guardar la pestaña.
 *
 * ══ SE APRUEBAN EN BLOQUE, NO DE A UNA ══════════════════════════════════════
 *
 * Hasta el 15-09-2026 cada fila traía su botón "Agregar" y había que apretarlos
 * uno por uno. Rodgers miró a alguien usarlo: leyó las filas, bajó al único
 * botón grande de la pantalla —"Guardar cambios"— y perdió todo lo propuesto.
 *
 * No fue un descuido de esa persona. El panel presenta N filas listas y abajo
 * hay un botón que dice "Guardar": nadie espera que ese botón ignore lo de
 * arriba. Hoy lo que queda acá se guarda con la pestaña, y rechazar una es
 * QUITARLA — un acto explícito igual que antes, pero del lado correcto.
 *
 * ══ POR QUÉ ALGUNAS PESTAÑAS NECESITAN ESTO Y OTRAS NO ══════════════════════
 *
 * En aguas y en suelos la propuesta se mete en el BORRADOR del formulario. En
 * maquinaria, instalaciones, construcciones y plantaciones no hay borrador: de
 * cada fila cuelgan FOTOS, así que cada una se crea con su propio POST —es el
 * mismo motivo por el que esas pestañas tienen un botón "Guardar máquina"
 * aparte del de la pestaña—. La propuesta no tiene dónde caer, así que se
 * muestra acá.
 *
 * ESO NO CAMBIÓ: guardar la pestaña dispara un alta por propuesta y después
 * escribe lo suyo. Lo que cambió es quién aprieta el botón, no cómo viaja.
 *
 * ══ NACIÓ GENÉRICO AL ENTRAR LA SEGUNDA (instalaciones, 14-09-2026) ═════════
 *
 * Con maquinaria sola era `MaquinasPropuestas` y sus columnas estaban escritas
 * adentro. Lo que se generalizó no es la tabla —cada pestaña tiene sus
 * columnas— sino las decisiones que la rodean, que son las que cuesta repetir
 * bien: no esconder lo que ya está, y decir dónde se guarda lo que queda.
 *
 * ══ NO ESCONDE LAS QUE YA ESTÁN ═════════════════════════════════════════════
 *
 * Una fila que ya está en el cuadro se lista igual, con el rótulo que lo
 * explica y sin botón —no hay nada que quitar de lo que no se va a guardar—.
 * Filtrarla sería esconder en silencio algo que el documento sí traía, y quien
 * mira la pantalla no tendría cómo saber que faltó.
 *
 * ══ VA EN SU PROPIA TARJETA Y NO DENTRO DEL AVISO ═══════════════════════════
 *
 * Son datos TABULARES, así que van en un `Table` del DS y no en tarjetas que
 * fingen ser filas. Y el `Table` pinta con `text-foreground`, que dentro del
 * panel `bg-info-soft` del aviso quedaría fuera de su paleta.
 */
export interface ColumnaPropuesta<T> {
  encabezado: string;
  celda: (fila: T) => ReactNode;
  /** Cifras: van en la tipografía monoespaciada del DS y sin cortar. */
  numerica?: boolean;
}

export function FilasPropuestas<T>({
  filas,
  columnas,
  titulo,
  yaEsta,
  ocupada,
  onQuitar,
  fuente,
}: {
  filas: T[];
  columnas: ColumnaPropuesta<T>[];
  /** Cómo se nombran: `(cuántas) => "3 máquinas propuestas por la IA"`. */
  titulo: (cantidad: number) => string;
  /** Si esta fila ya está en el cuadro. */
  yaEsta: (fila: T) => boolean;
  ocupada: boolean;
  /** Saca una de la lista, para que no se guarde. */
  onQuitar: (fila: T) => void;
  /**
   * De qué documento dice la IA que salió la fila. Si se pasa, se agrega una
   * columna "Fuente" —una sola vez acá y no en cada pestaña— y `null` se
   * dibuja como "Fuente no indicada". Es una columna y no una línea bajo la
   * fila porque el `Table` ya scrollea en horizontal a ~800 px, y una segunda
   * fila por propuesta rompería su semántica de tabla.
   */
  fuente?: (fila: T) => Fuente | null;
}) {
  if (filas.length === 0) return null;

  return (
    <div className="bg-card space-y-3 rounded-xl border p-5">
      <div>
        <h3 className="text-label text-ink">{titulo(filas.length)}</h3>
        <p className="text-caption text-ink-hint">
          Revísalas y quita las que no correspondan. Las que queden acá se guardan con
          «Guardar cambios», al pie de la pestaña.
        </p>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columnas.map((columna) => (
                <TableHead key={columna.encabezado}>{columna.encabezado}</TableHead>
              ))}
              {fuente ? <TableHead>Fuente</TableHead> : null}
              <TableHead>
                <span className="sr-only">Acciones</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filas.map((fila, indice) => {
              const repetida = yaEsta(fila);

              return (
                // El índice va en la clave porque una propuesta no tiene id —no
                // existe ninguna fila todavía— y dos obras pueden llamarse
                // igual. La lista no se reordena, así que el índice alcanza.
                <TableRow key={indice}>
                  {columnas.map((columna) => (
                    <TableCell
                      key={columna.encabezado}
                      className={columna.numerica ? "font-mono whitespace-nowrap" : undefined}
                    >
                      {columna.celda(fila)}
                    </TableCell>
                  ))}
                  {fuente ? (
                    <TableCell className="min-w-48 whitespace-normal">
                      <FuenteDeLaPropuesta fuente={fuente(fila)} />
                    </TableCell>
                  ) : null}
                  <TableCell className="whitespace-nowrap">
                    {repetida ? (
                      <span className="text-ink-hint text-caption">Ya está en el cuadro</span>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={ocupada}
                        onClick={() => onQuitar(fila)}
                      >
                        Quitar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
